import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getConfig } from '@edx/frontend-platform';
import { useIntl } from '@edx/frontend-platform/i18n';
import { getHttpClient } from '@edx/frontend-platform/auth';

import { SpinLoader } from '../../../components';
import { useHeaderFooter } from '../../../hooks';
import { ControllerFieldInput, FooterLinks } from '../components';
import { PASSWORD_RESET_INVALID_TOKEN, PASSWORD_RESET_SUCCESS } from '../Login/constants';
import ResetPasswordNotification from './ResetPasswordNotification';
import { validateFormFields } from './utils';
import {
  INVALID_TOKEN,
  TOO_MANY_ATTEMPTS,
  INTERNAL_SERVER_ERROR,
  PASSWORD_VALIDATION_FAILURE,
  RESET_PASSWORD_INITIAL_FORM_STATE,
} from './constants';
import type { FieldTypes, FieldErrorsTypes } from './types';

import '../Logistration.scss';
import messages from './messages';

export interface ResetPasswordProps {
  children: React.ReactNode;
}

const ResetPassword = ({ children }: ResetPasswordProps) => {
  const [formFields, setFormFields] = useState<FieldTypes>(RESET_PASSWORD_INITIAL_FORM_STATE);
  const [errors, setErrors] = useState<FieldErrorsTypes>({});
  const [notificationCode, setNotificationCode] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isValidating, setIsValidating] = useState<boolean>(true);
  const [success, setSuccess] = useState<boolean>(false);
  const [invalidToken, setInvalidToken] = useState<boolean>(false);

  const intl = useIntl();
  const { headerContent } = useHeaderFooter(children);
  const { params } = useParams<{ params?: string }>();
  const token = params || '';
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;
    const validateToken = async () => {
      try {
        const { data } = await getHttpClient().post(
          `${getConfig().LMS_BASE_URL}/user_api/v1/account/password_reset/token/validate/`,
          ({ token }),
          { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } },
        );

        if (!isMounted) {
          return;
        }
        if (data?.is_valid === false) {
          setInvalidToken(true);
        }
      } catch (error: any) {
        if (!isMounted) {
          return;
        }

        const code = error?.response?.data?.code;
        if (code === INVALID_TOKEN) {
          setInvalidToken(true);
        } else {
          setNotificationCode(INTERNAL_SERVER_ERROR);
        }
      } finally {
        if (isMounted) {
          setIsValidating(false);
        }
      }
    };

    validateToken();

    return () => {
      isMounted = false;
    };
  }, [token]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormFields(prev => ({ ...prev, [name]: value }));
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setErrors(prev => ({ ...prev, [e.target.name]: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validateFormFields(formFields, intl);
    if (validationErrors.new_password || validationErrors.confirm_password) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        new_password1: formFields.newPassword,
        new_password2: formFields.confirmPassword,
      };
      const url = new URL(`${getConfig().LMS_BASE_URL}/password/reset/${token}/`);
      const { data } = await getHttpClient().post(
        url.href,
        (payload),
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } },
      );

      if (data.reset_status) {
        setSuccess(true);
      } else {
        setNotificationCode(PASSWORD_VALIDATION_FAILURE);
      }
    } catch (error) {
      if (typeof error === 'object' && error !== null && 'response' in error) {
        const { response } = error as { response?: { data?: { code?: string }; status?: number } };
        const code = response?.data?.code;

        if (code === INVALID_TOKEN) {
          setInvalidToken(true);
          return;
        }
        setNotificationCode(response?.status === 429 ? TOO_MANY_ATTEMPTS : INTERNAL_SERVER_ERROR);
      } else {
        setNotificationCode(INTERNAL_SERVER_ERROR);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isValidating) {
    return <SpinLoader size={40} color="#000" />;
  }

  if (invalidToken) {
    navigate('/login', { state: { code: PASSWORD_RESET_INVALID_TOKEN } });
  }

  if (success) {
    navigate('/login', { state: { code: PASSWORD_RESET_SUCCESS } });
  }

  return (
    <div className="container-center-horizontal">
      <div className="logistration-main screen">
        {headerContent}
        <div className="logistration">
          <div className="auth-page-block">
            <div className="auth-page-main-content">
              <ResetPasswordNotification
                code={notificationCode || ''}
                count={0}
              />
              <h2 className="auth-page-title">{intl.formatMessage(messages.title)}</h2>
              <div className="auth-title-desc">{intl.formatMessage(messages.instructions)}</div>
              <form className="auth-form">
                <ControllerFieldInput
                  name="newPassword"
                  type="password"
                  placeholder={intl.formatMessage(messages.fieldNewPassword)}
                  value={formFields.newPassword}
                  handleChange={handleChange}
                  handleFocus={handleFocus}
                  error={errors.new_password}
                />
                <ControllerFieldInput
                  name="confirmPassword"
                  type="password"
                  placeholder={intl.formatMessage(messages.fieldConfirmPassword)}
                  value={formFields.confirmPassword}
                  handleChange={handleChange}
                  handleFocus={handleFocus}
                  error={errors.confirm_password}
                />
                <button
                  className="auth-submit-btn"
                  type="submit"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? <SpinLoader size={20} color="#fff" />
                    : intl.formatMessage(messages.reset)}
                </button>
              </form>
            </div>
            <FooterLinks />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
